import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, Building2, Clock3, CreditCard, Database, Mail, Shield, Users } from 'lucide-react';
import { EmptyState } from '../components/EmptyState';
import { forceInternalTenantBillingSync, getInternalTenantDetail, InternalTenantDetail, toggleInternalTenantAdvisor } from '../services/internalControlPlaneService';

type TenantTab = 'overview' | 'billing' | 'ai' | 'emails' | 'users' | 'audit';

const formatDateTime = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

interface InternalTenantDetailViewProps {
  companyId: string;
}

export const InternalTenantDetailView: React.FC<InternalTenantDetailViewProps> = ({ companyId }) => {
  const [activeTab, setActiveTab] = useState<TenantTab>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<InternalTenantDetail | null>(null);
  const [advisorReason, setAdvisorReason] = useState('');
  const [savingAdvisor, setSavingAdvisor] = useState(false);
  const [billingSyncReason, setBillingSyncReason] = useState('');
  const [syncingBilling, setSyncingBilling] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const detail = await getInternalTenantDetail(companyId);
        if (!cancelled) setData(detail);
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Unable to load tenant detail.';
          setError(message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [companyId]);

  const tabButtons: Array<{ id: TenantTab; label: string }> = useMemo(() => [
    { id: 'overview', label: 'Overview' },
    { id: 'billing', label: 'Billing' },
    { id: 'ai', label: 'AI Usage' },
    { id: 'emails', label: 'Emails' },
    { id: 'users', label: 'Users' },
    { id: 'audit', label: 'Audit' },
  ], []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-red-200 p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-red-800">Tenant detail unavailable</h3>
            <p className="text-sm text-red-600 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <EmptyState
        icon={<Building2 size={40} />}
        title="No tenant selected"
        subtitle="Provide a valid tenant context to inspect details."
      />
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h2 className="text-xl font-bold text-slate-800">{data.company.name}</h2>
            <p className="text-xs text-slate-500 mt-1">Tenant context: {data.company.id}</p>
          </div>
          <span className="inline-flex items-center gap-2 text-sm px-3 py-1 rounded-full bg-slate-100 text-slate-700 font-medium">
            <Shield size={14} />
            Plan: {data.company.plan}
          </span>
        </div>
      </div>

      <div className="w-full overflow-x-auto">
        <div className="flex space-x-1 bg-slate-200 p-1 rounded-xl w-fit min-w-full sm:min-w-0">
          {tabButtons.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id ? 'bg-toggle text-white shadow-sm' : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <InfoCard icon={<Database className="w-4 h-4 text-indigo-600" />} label="Tools" value={String(data.overview.toolCount)} />
            <InfoCard icon={<Users className="w-4 h-4 text-indigo-600" />} label="Users" value={String(data.overview.employeeCount)} />
            <InfoCard icon={<Shield className="w-4 h-4 text-indigo-600" />} label="Active admins" value={String(data.overview.adminCount)} />
            <InfoCard icon={<Clock3 className="w-4 h-4 text-indigo-600" />} label="Last activity" value={formatDateTime(data.overview.lastActivityAt)} />
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-lg font-semibold text-slate-800">Support Actions</h3>
            <p className="text-sm text-slate-500 mt-1">
              Toggle Advisor safely for this tenant. A reason is mandatory and every action is audited.
            </p>
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <span className={`text-xs font-semibold px-2 py-1 rounded-full w-fit ${data.company.advisorEnabled ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                Advisor {data.company.advisorEnabled ? 'Enabled' : 'Disabled'}
              </span>
              <textarea
                value={advisorReason}
                onChange={(e) => setAdvisorReason(e.target.value)}
                placeholder="Reason for this manual override"
                className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm min-h-[42px] max-h-24"
              />
              <button
                type="button"
                disabled={savingAdvisor}
                onClick={async () => {
                  const reason = advisorReason.trim();
                  if (!reason) {
                    alert('Reason is required.');
                    return;
                  }
                  const nextEnabled = !data.company.advisorEnabled;
                  const confirmText = nextEnabled
                    ? 'Enable Advisor for this tenant?'
                    : 'Disable Advisor for this tenant?';
                  if (!window.confirm(confirmText)) return;

                  setSavingAdvisor(true);
                  try {
                    await toggleInternalTenantAdvisor({
                      companyId: data.company.id,
                      advisorEnabled: nextEnabled,
                      reason,
                    });
                    const refreshed = await getInternalTenantDetail(companyId);
                    setData(refreshed);
                    setAdvisorReason('');
                  } finally {
                    setSavingAdvisor(false);
                  }
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium text-white ${
                  data.company.advisorEnabled ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'
                } disabled:bg-slate-400`}
              >
                {savingAdvisor
                  ? 'Applying...'
                  : data.company.advisorEnabled
                    ? 'Disable Advisor'
                    : 'Enable Advisor'}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'billing' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-slate-500" />
                Billing Snapshot
              </h3>
              <Link
                to={`/internal/tenants/${encodeURIComponent(data.company.id)}/billing-actions`}
                className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                Open billing actions
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <DataRow label="Plan" value={data.company.plan} />
              <DataRow label="Subscription status" value={data.company.subscriptionStatus || '—'} />
              <DataRow label="Billing email" value={data.company.billingEmail || '—'} />
              <DataRow label="Trial ends at" value={formatDateTime(data.company.trialEndsAt)} />
              <DataRow label="AI spending cap" value={`€${data.company.aiSpendingCap.toLocaleString()}`} />
              <DataRow label="Plan limits" value={data.company.planLimits ? 'Available' : '—'} />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
            <h3 className="text-lg font-semibold text-slate-800">Billing Sync Utility</h3>
            <p className="text-sm text-slate-500">
              Force-refresh Paddle subscription status for this tenant. This action is audited and requires a reason.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <DataRow label="Last sync time" value={formatDateTime(data.company.billingSyncLastAt)} />
              <DataRow label="Last sync status" value={data.company.billingSyncLastStatus || '—'} />
              <DataRow label="Last sync result" value={data.company.billingSyncLastMessage || '—'} />
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <textarea
                value={billingSyncReason}
                onChange={(e) => setBillingSyncReason(e.target.value)}
                placeholder="Reason for forcing billing sync"
                className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm min-h-[42px] max-h-24"
              />
              <button
                type="button"
                disabled={syncingBilling}
                onClick={async () => {
                  const reason = billingSyncReason.trim();
                  if (!reason) {
                    alert('Reason is required.');
                    return;
                  }
                  if (!window.confirm('Run force billing sync now?')) return;
                  setSyncingBilling(true);
                  try {
                    await forceInternalTenantBillingSync({
                      companyId: data.company.id,
                      reason,
                    });
                    const refreshed = await getInternalTenantDetail(companyId);
                    setData(refreshed);
                    setBillingSyncReason('');
                  } finally {
                    setSyncingBilling(false);
                  }
                }}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-400"
              >
                {syncingBilling ? 'Syncing...' : 'Force billing sync'}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'ai' && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
          <h3 className="text-lg font-semibold text-slate-800">AI Usage Snapshot</h3>
          {!data.aiUsage ? (
            <EmptyState icon={<Database size={32} />} title="No usage data" subtitle="No plan usage row found yet for this tenant." />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <InfoCard icon={<Database className="w-4 h-4 text-indigo-600" />} label="Queries used" value={String(data.aiUsage.aiQueriesUsed)} />
              <InfoCard icon={<Database className="w-4 h-4 text-indigo-600" />} label="Queries overage" value={String(data.aiUsage.aiQueriesOverage)} />
              <InfoCard icon={<Database className="w-4 h-4 text-indigo-600" />} label="Overage amount" value={`€${data.aiUsage.aiOverageAmount.toFixed(2)}`} />
            </div>
          )}
        </div>
      )}

      {activeTab === 'emails' && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
          <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Mail className="w-5 h-5 text-slate-500" />
            Email Health
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoCard icon={<Mail className="w-4 h-4 text-indigo-600" />} label="Sent last 30 days" value={String(data.emails.sentLast30d)} />
            <InfoCard icon={<Clock3 className="w-4 h-4 text-indigo-600" />} label="Last sent at" value={formatDateTime(data.emails.lastSentAt)} />
          </div>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-2">Template</th>
                  <th className="text-right px-4 py-2">Count</th>
                </tr>
              </thead>
              <tbody>
                {data.emails.templates.length === 0 ? (
                  <tr><td className="px-4 py-3 text-slate-500" colSpan={2}>No template usage in last 30 days.</td></tr>
                ) : data.emails.templates.map((item) => (
                  <tr key={item.template} className="border-t border-slate-100">
                    <td className="px-4 py-2">{item.template}</td>
                    <td className="px-4 py-2 text-right font-medium">{item.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Users</h3>
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-2">Name</th>
                  <th className="text-left px-4 py-2">Email</th>
                  <th className="text-left px-4 py-2">Role</th>
                  <th className="text-left px-4 py-2">Auth Role</th>
                  <th className="text-left px-4 py-2">Last login</th>
                </tr>
              </thead>
              <tbody>
                {data.users.length === 0 ? (
                  <tr><td className="px-4 py-3 text-slate-500" colSpan={5}>No users found for this tenant.</td></tr>
                ) : data.users.map((user) => (
                  <tr key={user.id} className="border-t border-slate-100">
                    <td className="px-4 py-2">{user.name}</td>
                    <td className="px-4 py-2">{user.email || '—'}</td>
                    <td className="px-4 py-2">{user.role || '—'}</td>
                    <td className="px-4 py-2">{user.authRole || '—'}</td>
                    <td className="px-4 py-2">{formatDateTime(user.lastLoginAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'audit' && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Audit</h3>
          <div className="space-y-3">
            {data.audit.length === 0 ? (
              <EmptyState icon={<Shield size={32} />} title="No audit events" subtitle="No entries in tenant audit log yet." />
            ) : data.audit.map((entry) => (
              <div key={entry.id} className="border border-slate-200 rounded-lg p-3">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-medium text-slate-800">{entry.message}</p>
                  <span className="text-xs text-slate-500">{formatDateTime(entry.createdAt)}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {entry.eventType}
                  {entry.toolName ? ` • Tool: ${entry.toolName}` : ''}
                  {entry.requesterName ? ` • Requester: ${entry.requesterName}` : ''}
                  {entry.newStatus ? ` • Status: ${entry.newStatus}` : ''}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const InternalTenantLookupView: React.FC = () => {
  const navigate = useNavigate();
  const [companyId, setCompanyId] = useState('');

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl">
      <h2 className="text-xl font-bold text-slate-800">Tenant Context</h2>
      <p className="text-sm text-slate-500 mt-1">
        Enter a tenant company ID to open the internal read-only diagnostic tabs.
      </p>
      <form
        className="mt-4 flex flex-col sm:flex-row gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          const normalized = companyId.trim();
          if (!normalized) return;
          navigate(`/internal/tenants/${encodeURIComponent(normalized)}`);
        }}
      >
        <input
          type="text"
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
          placeholder="company UUID"
          className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <button
          type="submit"
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
        >
          Open tenant detail
        </button>
      </form>
    </div>
  );
};

const InfoCard = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <div className="flex items-center justify-between gap-3">
      <p className="text-sm text-slate-500">{label}</p>
      <span>{icon}</span>
    </div>
    <p className="text-lg font-semibold text-slate-800 mt-2 break-words">{value}</p>
  </div>
);

const DataRow = ({ label, value }: { label: string; value: string }) => (
  <div className="border border-slate-200 rounded-lg px-3 py-2">
    <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
    <p className="text-sm font-medium text-slate-800 mt-1 break-words">{value}</p>
  </div>
);
